// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

export interface TokenUsage {
  totalTokens?: number;
  promptTokens?: number;
  completionTokens?: number;
}

export interface AssessmentReport {
  user_id: number;
  user_name: string;
  email_id: string;

  assessment_id: string;
  functional_area: string | null;
  skill: string | null;
  assessment_mode: string;
  status: string;

  expertise_level: string | null;
  assessed_level: number | null;
  level_label: string | null;

  assessment_start_time: string | null;
  assessment_end_time: string | null;
  total_time_taken: number | null;

  overall_score: number | null;

  voice_ai_probability: string | number | null;
  voice_is_likely_ai: boolean | null;
  voice_confidence: string | null;

  transcript_ai_probability: string | number | null;
  combined_ai_probability: string | number | null;
  combined_confidence: string | null;

  total_tokens_used: number | null;

  assessment_token_usage: TokenUsage | null;

  voice_analysis_count: number | string | null;

  created_date: string;
  updated_date: string;
}

const formatDate = (date: string | null): string => {
  if (!date) return '—';
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return '—';
  return parsedDate.toLocaleString();
};

const formatValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '' || value === 'NaN' || value === 'null') {
    return '—';
  }
  return String(value);
};

export const formatScore = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '' || value === 'NaN' || value === 'null') {
    return '—';
  }
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return '—';
  return String(Math.round(numericValue));
};

const formatProbability = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined || value === '' || value === 'NaN') return '—';
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return '—';
  return `${(numericValue * 100).toFixed(1)}%`;
};

const formatDuration = (seconds: number | null): string => {
  if (seconds === null || seconds === undefined) return '—';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes === 0) return `${remainingSeconds}s`;
  return `${minutes}m ${remainingSeconds}s`;
};

const getStatusClass = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'status-completed';
    case 'started':
      return 'status-started';
    default:
      return 'status-default';
  }
};

interface AssessmentModalProps {
  report: AssessmentReport;
  onClose: () => void;
}

const AssessmentModal = ({ report, onClose }: AssessmentModalProps): JSX.Element => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()}>
        <div className="assessment-card-header">
          <div>
            <h4>Assessment #{report.assessment_id}</h4>
            <span>{formatValue(report.functional_area)}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className={`status-badge ${getStatusClass(report.status)}`}>{formatValue(report.status)}</span>
            <button type="button" className="modal-close-button" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </div>

        <div className="assessment-details-grid">
          <div>
            <label>User</label>
            <span>{formatValue(report.user_name)}</span>
          </div>
          <div>
            <label>Email</label>
            <span>{formatValue(report.email_id)}</span>
          </div>
          <div>
            <label>Functional Area</label>
            <span>{formatValue(report.functional_area)}</span>
          </div>
          <div>
            <label>Skill</label>
            <span>{formatValue(report.skill)}</span>
          </div>
          <div>
            <label>Assessment Mode</label>
            <span>{formatValue(report.assessment_mode)}</span>
          </div>
          <div>
            <label>Expertise Level</label>
            <span>{formatValue(report.expertise_level)}</span>
          </div>
          <div>
            <label>Assessed Level</label>
            <span>{formatValue(report.assessed_level)}</span>
          </div>
          <div>
            <label>Level Label</label>
            <span>{formatValue(report.level_label)}</span>
          </div>
          <div>
            <label>Overall Score</label>
            <span>{formatScore(report.overall_score)}</span>
          </div>
          <div>
            <label>Total Time</label>
            <span>{formatDuration(report.total_time_taken)}</span>
          </div>
          <div>
            <label>Start Time</label>
            <span>{formatDate(report.assessment_start_time)}</span>
          </div>
          <div>
            <label>End Time</label>
            <span>{formatDate(report.assessment_end_time)}</span>
          </div>
        </div>

        <div className="report-subsection">
          <h4>Token Usage</h4>
          <div className="metrics-grid">
            <div>
              <label>Total Tokens</label>
              <strong>{formatValue(report.total_tokens_used)}</strong>
            </div>
            <div>
              <label>Prompt Tokens</label>
              <strong>{formatValue(report.assessment_token_usage?.promptTokens)}</strong>
            </div>
            <div>
              <label>Completion Tokens</label>
              <strong>{formatValue(report.assessment_token_usage?.completionTokens)}</strong>
            </div>
          </div>
        </div>

        <div className="report-subsection">
          <h4>AI Detection</h4>
          <div className="metrics-grid">
            <div>
              <label>Voice AI Probability</label>
              <strong>{formatProbability(report.voice_ai_probability)}</strong>
            </div>
            <div>
              <label>Voice Confidence</label>
              <strong>{formatValue(report.voice_confidence)}</strong>
            </div>
            <div>
              <label>Transcript AI Probability</label>
              <strong>{formatProbability(report.transcript_ai_probability)}</strong>
            </div>
            <div>
              <label>Combined AI Probability</label>
              <strong>{formatProbability(report.combined_ai_probability)}</strong>
            </div>
            <div>
              <label>Combined Confidence</label>
              <strong>{formatValue(report.combined_confidence)}</strong>
            </div>
            <div>
              <label>Voice Analysis Count</label>
              <strong>{formatValue(report.voice_analysis_count)}</strong>
            </div>
            <div>
              <label>Likely AI Generated Voice</label>
              <strong>{report.voice_is_likely_ai ? 'Yes' : 'No'}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentModal;
